/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk } from '@reduxjs/toolkit'
import { IFaxTag } from '@dataTypes/fax.type'
import {
  createFaxTag,
  deleteFaxTagById,
  getAllFaxTag,
  updateFaxTagById,
} from '@services/api/fax/tag.api'
import { RootState } from '@store/index'

export const fetchAllFaxTags = createAsyncThunk<
  IFaxTag[],
  void | { dbName?: string },
  { rejectValue: string }
>('api/fax/tag/fetch/all', async (arg, { rejectWithValue }) => {
  try {
    const dbName =
      typeof arg === 'object' && arg?.dbName != null ? arg.dbName : undefined
    const response = await getAllFaxTag(dbName)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    if (
      response.data &&
      'fax_tag' in response.data &&
      Array.isArray(response.data.fax_tag)
    ) {
      return response.data.fax_tag as IFaxTag[]
    }

    return rejectWithValue('Fax tags not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addFaxTag = createAsyncThunk<
  IFaxTag,
  IFaxTag,
  { rejectValue: string }
>('api/fax/tag/add', async (data, { getState, dispatch, rejectWithValue }) => {
  try {
    const state = getState() as RootState
    const clinicId = state.clinic.selectedClinic?.clinic_id as string
    const response = await createFaxTag({
      ...data,
      clinic_id: clinicId,
    })
    if (response.statusCode !== 201) {
      return rejectWithValue(response.message || 'Failed to add fax tag')
    }
    if (response.data && 'fax_tag' in response.data && response.data.fax_tag) {
      const tag = response.data.fax_tag as IFaxTag
      await dispatch(fetchAllFaxTags())
      return tag
    }
    return rejectWithValue('Fax tag not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to create fax tag')
  }
})

export const updateFaxTag = createAsyncThunk<
  IFaxTag,
  { fax_tag_id: string; data: Partial<IFaxTag> },
  { rejectValue: string }
>(
  'api/fax/tag/update',
  async ({ fax_tag_id, data }, { dispatch, rejectWithValue }) => {
    try {
      const response = await updateFaxTagById(fax_tag_id, data)
      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }
      if (
        response.data &&
        'fax_tag' in response.data &&
        response.data.fax_tag
      ) {
        await dispatch(fetchAllFaxTags())
        return response.data.fax_tag as IFaxTag
      }
      return rejectWithValue('Fax tag not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const deleteFaxTag = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>('api/fax/tag/delete', async (id, { dispatch, rejectWithValue }) => {
  try {
    const response = await deleteFaxTagById(id)
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }
    await dispatch(fetchAllFaxTags())
    return id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})
